/**
 * DemoBanner — баннер демо-сессии: счётчик, кнопка выхода, модал «5 мин до конца».
 *
 * Поведение:
 *  - На timeLeft <= 5:00 (и если ещё не показывали для текущего expires) → блокирующий модал.
 *    • Если demo_extended=false → 2 кнопки: «Продлить +1ч» / «Завершить сейчас».
 *    • Если demo_extended=true  → только «Завершить сейчас» + CTA «Зарегистрироваться».
 *  - На timeLeft <= 0 → POST /demo/force-expire + logout + редирект на /?reason=demo_expired.
 *
 * PostHog:
 *  - demo_warning_shown
 *  - demo_extend_clicked / demo_extend_succeeded / demo_extend_failed
 *  - demo_force_logout
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Analytics } from '../analytics';

const BASE = process.env.REACT_APP_API_URL || '/api';
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

function formatTimeLeft(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function DemoBanner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expiresAt, setExpiresAt] = useState(null);
  const [extended, setExtended] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');
  const forcingRef = useRef(false);          // защита от двойного force-expire
  const warningShownForRef = useRef(null);    // expiresAt, для которого уже показали модал
  const analyticsFiredForRef = useRef(null);  // expiresAt, для которого уже зарепортили warning_shown
  const [tourDone, setTourDone] = useState(false); // гайд пройден → кнопка «Создать аккаунт»

  // Следим за состоянием гайд-тура: после 'done' кнопка в баннере превращается
  // в «Создать аккаунт».
  useEffect(() => {
    try {
      const orgId = user?.organization_id;
      if (orgId) {
        const saved = JSON.parse(localStorage.getItem(`pm_demo_tour_${orgId}`));
        if (saved?.seenConvert) setTourDone(true);
      }
    } catch {}
    // «Создать аккаунт» показываем только когда гайд дошёл до экрана конверсии,
    // а не при раннем «Осмотрюсь сам».
    const onStep = (e) => setTourDone(!!e.detail?.seenConvert);
    window.addEventListener('demo-tour-step', onStep);
    return () => window.removeEventListener('demo-tour-step', onStep);
  }, [user]);

  // «Создать аккаунт» из баннера: сбрасываем демо-сессию и уходим на регистрацию
  // с нуля (full reload, чтобы /register отрисовался неавторизованным).
  const handleCreateAccount = () => {
    Analytics.demoBannerCtaClicked?.({ source: 'banner_done' });
    const params = new URLSearchParams();
    if (user?.email) params.set('email', user.email);
    if (user?.name) params.set('name', user.name);
    let ref = null;
    try { ref = sessionStorage.getItem('referral_ref'); } catch {}
    if (ref) params.set('ref', ref);
    try { localStorage.removeItem('pm_token'); } catch {}
    window.location.assign(`/register?${params.toString()}`);
  };

  const fetchInfo = () => {
    if (!user) return;
    fetch(`${BASE}/demo/info`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('pm_token')}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.is_demo && data?.demo_expires_at) {
          setExpiresAt(new Date(data.demo_expires_at));
          setExtended(!!data.demo_extended);
        }
      })
      .catch(() => {});
  };

  // Получаем demo_expires_at один раз при монтировании
  useEffect(() => { fetchInfo(); /* eslint-disable-next-line */ }, [user]);

  // Тикаем таймер + триггерим модал и force-expire
  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => {
      const left = expiresAt - Date.now();
      setTimeLeft(left);

      // Показ модала при пересечении 5-минутного порога — один раз на каждое expires
      if (
        left <= WARNING_THRESHOLD_MS &&
        left > 0 &&
        warningShownForRef.current !== expiresAt.getTime()
      ) {
        warningShownForRef.current = expiresAt.getTime();
        setWarningOpen(true);
        if (analyticsFiredForRef.current !== expiresAt.getTime()) {
          analyticsFiredForRef.current = expiresAt.getTime();
          Analytics.demoWarningShown?.({ extended });
        }
      }

      // Завершение по 0:00 — клиентский путь
      if (left <= 0 && !forcingRef.current) {
        forcingRef.current = true;
        Analytics.demoForceLogout?.();
        fetch(`${BASE}/demo/force-expire`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('pm_token')}` },
        }).catch(() => {}).finally(() => {
          logout();
          window.location.href = '/?reason=demo_expired';
        });
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, extended, logout]);

  // Не показываем баннер если не демо (expiresAt не пришёл)
  if (!expiresAt) return null;

  const expired = timeLeft !== null && timeLeft <= 0;

  const handleExit = async () => {
    if (!window.confirm(t('demo.exitConfirm'))) return;
    Analytics.demoExited?.();
    try {
      await fetch(`${BASE}/demo/exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('pm_token')}`,
        },
      });
    } catch {}
    logout();
    navigate('/login');
  };

  const handleExtend = async () => {
    if (extending) return;
    setExtending(true);
    setExtendError('');
    Analytics.demoExtendClicked?.();
    try {
      const r = await fetch(`${BASE}/demo/extend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('pm_token')}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'extend failed');
      // Обновляем локально
      setExpiresAt(new Date(data.demo_expires_at));
      setExtended(true);
      setWarningOpen(false);
      // Сбрасываем флаги — для нового expires модал покажется ещё раз
      warningShownForRef.current = null;
      analyticsFiredForRef.current = null;
      Analytics.demoExtendSucceeded?.();
    } catch (e) {
      setExtendError(e.message || 'Ошибка');
      Analytics.demoExtendFailed?.({ error: e.message });
    } finally {
      setExtending(false);
    }
  };

  const handleRegister = () => {
    Analytics.demoBannerCtaClicked?.({ source: 'warning_modal' });
    const savedRef = sessionStorage.getItem('referral_ref');
    const params = new URLSearchParams({ email: user?.email || '' });
    if (user?.name) params.set('name', user.name);
    if (savedRef) params.set('ref', savedRef);
    navigate(`/register?${params.toString()}`);
  };

  return (
    <>
      <div
        data-ph-capture-attribute-component="demo_banner"
        style={{
          background: 'linear-gradient(90deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.06) 100%)',
          borderBottom: '1px solid rgba(99,102,241,0.2)',
          padding: '8px 12px 8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 16 }}>🧪</span>
        <span style={{ fontWeight: 600, color: 'var(--accent, #6366f1)' }}>
          {t('demo.demoBanner')}
        </span>
        {timeLeft !== null && (
          <span style={{ color: expired ? '#ef4444' : 'var(--text2)', fontVariantNumeric: 'tabular-nums' }}>
            {expired ? '0:00' : formatTimeLeft(timeLeft)}
          </span>
        )}
        {tourDone ? (
          <button
            onClick={handleCreateAccount}
            data-ph-capture-attribute-action="create_account_from_banner"
            title={t('demo.tour.createAccountBtn')}
            style={{
              marginLeft: 'auto', flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'rgba(99,102,241,1)', color: '#fff',
              border: 'none', padding: '5px 14px', borderRadius: 999,
              fontSize: 12, fontWeight: 700, cursor: 'pointer', lineHeight: 1.4,
            }}
          >
            🚀 {t('demo.tour.createAccountBtn')}
          </button>
        ) : (
          <button
            onClick={() => { Analytics.demoGuideReopen?.(); window.dispatchEvent(new CustomEvent('demo-resume-tour')); }}
            data-ph-capture-attribute-action="resume_guide"
            title={t('demo.tour.guideButtonTitle')}
            style={{
              marginLeft: 'auto', flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'rgba(99,102,241,0.14)', color: 'var(--accent, #6366f1)',
              border: 'none', padding: '4px 12px', borderRadius: 999,
              fontSize: 12, fontWeight: 700, cursor: 'pointer', lineHeight: 1.4,
            }}
          >
            <span style={{ fontSize: 13 }}>▶</span>{t('demo.tour.guideButton')}
          </button>
        )}
        <button
          onClick={handleExit}
          data-ph-capture-attribute-action="exit_demo"
          style={{
            background: 'transparent', border: 'none', padding: '2px 6px',
            fontSize: 15, color: 'var(--text2)', cursor: 'pointer',
            lineHeight: 1, opacity: 0.5, flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      {warningOpen && timeLeft > 0 && (
        <div
          data-ph-capture-attribute-component="demo_warning_modal"
          data-ph-capture-attribute-extended={String(extended)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'var(--bg2)', borderRadius: 12,
              maxWidth: 440, width: '100%', padding: '24px 24px 20px',
              boxShadow: '0 12px 48px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
              {t('demo.warning.title')}
            </div>
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.45 }}>
              {extended ? t('demo.warning.bodyAfterExtend') : t('demo.warning.body')}
            </div>
            <div style={{
              fontSize: 28, fontWeight: 700, color: 'var(--accent, #6366f1)',
              fontVariantNumeric: 'tabular-nums', marginBottom: 20, textAlign: 'center',
            }}>
              {formatTimeLeft(timeLeft)}
            </div>

            {extendError && (
              <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>
                {extendError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
              {!extended && (
                <button
                  onClick={handleExtend}
                  disabled={extending}
                  data-ph-capture-attribute-action="extend_demo"
                  style={{
                    background: 'var(--accent, #6366f1)', color: '#fff', border: 'none',
                    padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: extending ? 'wait' : 'pointer',
                    opacity: extending ? 0.6 : 1,
                  }}
                >
                  {extending ? t('demo.warning.extending') : t('demo.warning.extend')}
                </button>
              )}
              {extended && (
                <button
                  onClick={handleRegister}
                  data-ph-capture-attribute-action="register_from_warning"
                  style={{
                    background: 'var(--accent, #6366f1)', color: '#fff', border: 'none',
                    padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('demo.warning.register')}
                </button>
              )}
              <button
                onClick={handleExit}
                data-ph-capture-attribute-action="end_demo_from_warning"
                style={{
                  background: 'transparent', color: 'var(--text2)',
                  border: '1px solid var(--border, #e5e7eb)',
                  padding: '12px 16px', borderRadius: 8, fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                {t('demo.warning.endNow')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
